import { Button, Checkbox, Col, Form, GetProp, Input, Row, Upload, UploadProps } from 'antd';
import { useEffect, useState } from 'react';
import { FormProps } from 'antd/es/form/Form';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

// services
import NotificationService from 'core/services/notification';
import { onCreateAdmin } from 'modules/admins/services/admins';
import { onGetAllRoles } from 'modules/roles/services/roles';

// models
import { Role } from 'core/models/user';

// components
import BackButton from 'components/backButton/backButton';

// styles
import styles from './create.module.scss';
import { useTranslation } from 'react-i18next';
import PhoneInputComponent, { PhoneInputReturn } from 'components/phoneInput/phoneInput';
import { checkPhoneValidation } from 'utils/phone';

type FieldType = {
	name: string;
	email: string;
	password: string;
	phone_country_code: string;
	phone_number: string;
	avatar: any;
};

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

function CreateAdminPage() {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [createForm] = Form.useForm();
	const [roles, setRoles] = useState<Role[]>([]);
	const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
	const [imageUrl, setImageUrl] = useState<string>();

	useEffect(() => {
		getRoles();
	}, []);

	const validatePhone = (_: any, value: any) => {
		const phone = createForm.getFieldValue('phone_country_code') + value;
		if (checkPhoneValidation(phone)) {
			return Promise.resolve();
		} else if (phone) return Promise.reject(new Error(t('COMMON.PHONE_INVALID')));
		else return Promise.reject();
	};

	const getRoles = async () => {
		const response = await onGetAllRoles();
		setRoles(response.data.data.roles);
	};

	const inputStyles = {
		width: 500,
		maxWidth: '100%',
	};

	const onRoleChange = (id: number) => {
		const idIndex = selectedRoles.findIndex((el) => el === id);

		if (idIndex >= 0) {
			const updatedPermissions = selectedRoles.filter((_, index) => index !== idIndex);
			setSelectedRoles(updatedPermissions);
		} else {
			const updatedPermissions = [...selectedRoles, id];
			setSelectedRoles(updatedPermissions);
		}
	};

	const handlePhoneChange = ({ dialCode, number, isValid }: PhoneInputReturn) => {
		if (isValid) {
			createForm.setFieldsValue({
				phone_country_code: dialCode,
				phone_number: number,
			});
		} else {
			createForm.setFields([
				{
					name: 'phone_number',
					errors: [t('COMMON.INVALID_PHONE')],
				},
			]);
		}
	};

	const onSubmit: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setIsSubmitting(true);

			const createAdminPayload = new FormData();
			createAdminPayload.append('name', values.name);
			createAdminPayload.append('email', values.email);
			createAdminPayload.append('password', values.password);
			createAdminPayload.append('avatar', values.avatar);
			createAdminPayload.append('password_confirmation', values.password);
			createAdminPayload.append('locale', 'en');
			createAdminPayload.append('active', '1');
			createAdminPayload.append('phone_country_code', values.phone_country_code.replace('+', ''));
			createAdminPayload.append('phone_number', values.phone_number);

			selectedRoles.forEach((role, i) => {
				createAdminPayload.append(`roles[${i}]`, role.toString());
			});

			await onCreateAdmin(createAdminPayload);
			NotificationService.success(t('COMMON.SUCCESS'), t('ADMINS.CREATED'));
			navigate('/admins');
		} catch (error) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.OPS'), t('ADMINS.NOT_CREATED'));
		}
	};

	const onFinishFailed: FormProps<FieldType>['onFinishFailed'] = (errorInfo) => {
		createForm.scrollToField(errorInfo.errorFields[0].name, {
			behavior: (el) => {
				el.length > 0 &&
					window.scrollTo({
						top: el[0].top - 80,
						behavior: 'smooth',
					});
			},
		});
	};

	const beforeUpload = (file: FileType) => {
		const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
		if (!isJpgOrPng) {
			NotificationService.error(t('COMMON.ONLY_PNG_JPG'));
			return false;
		}

		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(setImageUrl(reader.result as string));
		});

		return false;
	};

	const handleChange: UploadProps['onChange'] = (info) => {
		createForm.setFieldsValue({ avatar: info.file });
	};

	return (
		<>
			<div className="pageHeader">
				<BackButton />
				<h3>{t('ADMINS.ADD_NEW_ADMIN')}</h3>
			</div>

			<div className="sectionContainer">
				<h5 style={{ marginBottom: 20 }}>{t('ADMINS.INVITE_ADMINS')}</h5>
				{/* <h5 style={{ marginBottom: 20 }}>Send invites</h5> */}

				<Form name="createForm" form={createForm} onFinish={onSubmit} onFinishFailed={onFinishFailed}>
					<Row gutter={[20, 20]}>
						<Col span={24} md={12}>
							<h6>{t('ADMINS.INFORMATION')}</h6>
							<Form.Item<FieldType> name="name" rules={[{ required: true, message: t('ADMINS.FULL_NAME_REQUIRED') }]}>
								<Input size="large" placeholder={t('ADMINS.FULL_NAME')} style={inputStyles} />
							</Form.Item>

							<Form.Item<FieldType>
								name="email"
								rules={[
									{ required: true, message: t('ADMINS.EMAIL_REQUIRED') },
									{ type: 'email', message: t('ADMINS.EMAIL_NOT_VALID') },
								]}>
								<Input size="large" placeholder={t('ADMINS.EMAIL')} style={inputStyles} />
							</Form.Item>

							<Form.Item<FieldType>
								name="password"
								rules={[
									{ required: true, message: t('ADMINS.PASSWORD_REQUIRED') },
									{ min: 8, message: t('ADMINS.PASSWORD_8_CHARS') },
								]}>
								<Input.Password placeholder={t('ADMINS.PASSWORD')} style={inputStyles} size="large" />
							</Form.Item>

							<div className="mt-3">
								<Form.Item<FieldType> name="phone_country_code" hidden>
									<Input />
								</Form.Item>
								<Form.Item<FieldType>
									name="phone_number"
									rules={[{ required: true, message: t('COMMON.PHONE_REQUIRED') }, { validator: validatePhone }]}>
									<PhoneInputComponent onChange={handlePhoneChange} />
								</Form.Item>
							</div>
						</Col>
						<Col span={24} md={12}>
							<div>
								<h6>{t('ADMINS.AVATAR')}</h6>
								<Form.Item<FieldType> name="avatar" hidden>
									<Input />
								</Form.Item>
								<Upload
									className="squareInput"
									listType="picture-card"
									showUploadList={false}
									beforeUpload={beforeUpload}
									onChange={handleChange}>
									{imageUrl ? (
										<img src={imageUrl} alt="avatar" style={{ width: '100%' }} />
									) : (
										<button style={{ border: 0, background: 'none' }} type="button">
											{isSubmitting ? <LoadingOutlined /> : <PlusOutlined />}
											<div style={{ marginTop: 8 }}>{t('COMMON.UPLOAD')}</div>
										</button>
									)}
								</Upload>
							</div>
						</Col>
					</Row>
				</Form>

				<div className="my-5">
					<h5 className="mb-3">{t('ADMINS.ADMIN_ROLES')}</h5>
					{roles.map((role, i) => (
						<div className={styles.roleSelection} key={i}>
							<Checkbox onChange={() => onRoleChange(role.id)}>{role.name}</Checkbox>
						</div>
					))}
				</div>

				<Form.Item>
					<Button
						size="middle"
						type="primary"
						htmlType="submit"
						form="createForm"
						loading={isSubmitting}
						style={{ marginTop: 20, display: 'flex' }}>
						{t('ADMINS.SEND_STAFF_INVITATION')}
					</Button>
				</Form.Item>
			</div>
		</>
	);
}

export default CreateAdminPage;
